/**
 * Projects API
 *
 * The API endpoints for managing Projects in Dispatch Hub.
 */
import axios, { AxiosResponse } from 'axios'
import qs from 'qs'
import { del, get, post, put } from './base-strict'
import { SearchProjectOptions } from './useProjectsQueries'
import type { ProjectJson, ProjectListResponse } from '@ironsight/types'
import Project from '@/models/project'

const PROJECTS_ENDPOINT_V3 = '/api/v3/projects'
const PROJECTS_ENDPOINT_V4 = '/api/v4/projects'

export interface ActionResult<T> {
  data: T
}

export function createProject(
  projectName: string,
  ownerId: string | null,
): Promise<AxiosResponse<string>> {
  const source = axios.CancelToken.source()
  return post(PROJECTS_ENDPOINT_V3, {
    data: {
      name: projectName,
      owner_id: ownerId,
    },
    cancelToken: source.token,
  })
}

export function getProjects(
  searchOptions: SearchProjectOptions,
): Promise<AxiosResponse<ProjectListResponse>> {
  const source = axios.CancelToken.source()
  return get(`${PROJECTS_ENDPOINT_V4}`, {
    params: {
      offset: searchOptions.offset,
      limit: searchOptions.limit,
      query: searchOptions.query,
    },
    paramsSerializer: (params) => {
      return qs.stringify(params, { arrayFormat: 'repeat' })
    },
    cancelToken: source.token,
  })
}

export function getProject(projectId: string): Promise<AxiosResponse<ProjectJson>> {
  const source = axios.CancelToken.source()
  return get(`${PROJECTS_ENDPOINT_V3}/${projectId}`, {
    cancelToken: source.token,
  })
}

export function addJobToProject(projectId: string, jobId: number): Promise<AxiosResponse> {
  const source = axios.CancelToken.source()
  return post(`${PROJECTS_ENDPOINT_V3}/${projectId}/jobs`, {
    data: {
      job_id: jobId,
    },
    cancelToken: source.token,
  })
}

export function addJobsToProject(projectId: string, jobIds: string[]): Promise<AxiosResponse> {
  const source = axios.CancelToken.source()
  return post(`${PROJECTS_ENDPOINT_V4}/${projectId}/jobs`, {
    data: {
      job_ids: jobIds,
    },
    cancelToken: source.token,
  })
}

export function removeJobFromProject(projectId: string, jobNumber: number): Promise<AxiosResponse> {
  const source = axios.CancelToken.source()
  return del(`${PROJECTS_ENDPOINT_V3}/${projectId}/jobs/${jobNumber}`, {
    cancelToken: source.token,
  })
}

export function editProject(
  projectId: string,
  projectName: string,
): Promise<AxiosResponse<Project>> {
  const source = axios.CancelToken.source()
  return put(`${PROJECTS_ENDPOINT_V3}/${projectId}`, {
    data: {
      name: projectName,
    },
    cancelToken: source.token,
  })
}
